import { useState } from "react";
import { Package, ArrowDownToLine, ArrowUpFromLine, AlertTriangle, FileSignature, Search, X, ScanBarcode } from "lucide-react";
import { toast } from "sonner";
import { GudangScanDialog } from "@/components/barang/GudangScanDialog";
import { GudangRiwayatDialog } from "@/components/barang/GudangRiwayatDialog";

const fmtQty = (n) => Number(n ?? 0).toLocaleString("id-ID", { maximumFractionDigits: 2 });
const fmtTglPendek = (t) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(t || ""));
  if (m) return `${m[3]}/${m[2]}/${m[1]}`;
  const d = new Date(t);
  return isNaN(d) ? "-" : `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
};

// Slide 2: pengambilan barang keluar yang belum unggah tanda terima (dikelompokkan per kejadian)
export function SlideTTD({ ttPending }) {
  return (
    <div key="slide-ttd" data-testid="live-stock-slide-ttd" className="animate-in fade-in duration-200">
      <h2 className="text-base font-extrabold tracking-wide text-amber-600 flex items-center gap-2 mb-4">
        <FileSignature className="h-5 w-5" /> BARANG KELUAR — TANDA TERIMA BELUM DIUNGGAH
      </h2>
      {ttPending.length === 0 ? (
        <div className="py-24 text-center text-emerald-600" data-testid="live-stock-ttd-empty">
          <FileSignature className="h-10 w-10 mx-auto mb-3 text-emerald-300" />
          Semua tanda terima barang keluar sudah diunggah.
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
            {ttPending.slice(0, 9).map((g) => (
              <div key={g.key} data-testid={`live-stock-ttd-card-${g.key}`}
                className="rounded-xl border border-amber-200 bg-amber-50 p-4 shadow-sm">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-sm sm:text-base font-bold text-slate-800 leading-snug truncate">
                    {g.pengambil || "-"}
                  </p>
                  <span className="shrink-0 rounded-full bg-amber-100 border border-amber-300 px-2 py-0.5 text-[11px] font-bold text-amber-700">
                    {g.jumlah_barang} barang · {Number(g.total_qty ?? 0).toLocaleString("id-ID")} qty
                  </span>
                </div>
                <p className="text-[11px] text-slate-500 mt-1 truncate">
                  {g.lokasi_area || "-"}{g.kode_keluar ? ` · ${g.kode_keluar}` : ""}
                </p>
                <p className="text-[11px] text-slate-600 mt-1 line-clamp-2">
                  {g.items.slice(0, 3).map((i) => `${i.nama_barang} (${Number(i.qty ?? 0).toLocaleString("id-ID")})`).join(", ")}
                  {g.items.length > 3 ? `, +${g.items.length - 3} lainnya` : ""}
                </p>
                <p className="text-[11px] text-amber-700 font-semibold mt-1">
                  Keluar {fmtTglPendek(g.tanggal)} · perlu TTD & upload bukti
                </p>
              </div>
            ))}
          </div>
          {ttPending.length > 9 && (
            <p className="mt-3 text-sm text-amber-600 font-semibold" data-testid="live-stock-ttd-more">
              +{ttPending.length - 9} pengambilan lainnya
            </p>
          )}
        </>
      )}
    </div>
  );
}

// Slide 1: barang stok menipis
export function SlideMenipis({ lowStock }) {
  return (
    <div key="slide-menipis" data-testid="live-stock-slide-menipis" className="animate-in fade-in duration-500">
      <h2 className="text-base font-extrabold tracking-wide text-rose-600 flex items-center gap-2 mb-4">
        <AlertTriangle className="h-5 w-5" /> STOK MENIPIS — SEGERA RESTOCK
      </h2>
      {lowStock.length === 0 ? (
        <div className="py-24 text-center text-emerald-600" data-testid="live-stock-menipis-empty">
          <Package className="h-10 w-10 mx-auto mb-3 text-emerald-300" />
          Semua stok aman — tidak ada barang menipis.
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-3">
            {lowStock.slice(0, 12).map((b) => (
              <div key={b.id} data-testid={`live-stock-menipis-card-${b.kode}`}
                className="rounded-xl border border-rose-200 bg-rose-50 p-4 shadow-sm">
                <p className="font-mono text-[11px] text-rose-400">{b.kode}</p>
                <p className="text-sm sm:text-base font-bold text-slate-800 leading-snug line-clamp-2">{b.nama}</p>
                <p className="mt-2 text-3xl font-extrabold tabular-nums text-rose-600">
                  {fmtQty(b.stok_akhir)}<span className="text-xs font-semibold text-rose-400 ml-1">{b.satuan || ""}</span>
                </p>
                <p className="text-[11px] text-slate-500">Stok min: {fmtQty(b.stok_minimum)}</p>
              </div>
            ))}
          </div>
          {lowStock.length > 12 && (
            <p className="mt-3 text-sm text-rose-500 font-semibold" data-testid="live-stock-menipis-more">
              +{lowStock.length - 12} barang menipis lainnya
            </p>
          )}
        </>
      )}
    </div>
  );
}

// Slide 0: tabel aktivitas transaksi terbaru
export function LiveStockTable({ items }) {
  return (
    <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
      <table className="w-full" data-testid="live-stock-table">
        <thead>
          <tr className="bg-slate-100 text-left text-[11px] uppercase tracking-wider text-slate-500">
            <th className="px-4 py-3">Kode</th>
            <th className="px-4 py-3">Nama Barang</th>
            <th className="px-4 py-3 text-center">Masuk</th>
            <th className="px-4 py-3 text-center">Keluar</th>
            <th className="px-4 py-3 text-right">Stok Akhir</th>
            <th className="px-4 py-3 text-center">Status</th>
          </tr>
        </thead>
        <tbody>
          {items.map((b) => (
            <tr key={b.id} data-testid={`live-stock-row-${b.kode}`}
              className={`border-t border-slate-100 ${b.menipis ? "bg-rose-50" : "odd:bg-slate-50/60"}`}>
              <td className="px-4 py-3 font-mono text-sm text-slate-500 whitespace-nowrap">{b.kode}</td>
              <td className="px-4 py-3 text-base sm:text-lg font-semibold text-slate-800">{b.nama}</td>
              <td className="px-4 py-3 text-center">
                {b.masuk_periode > 0 ? (
                  <span className="inline-flex items-center gap-1 text-emerald-600 font-bold text-base">
                    <ArrowDownToLine className="h-4 w-4" /> {fmtQty(b.masuk_periode)}
                  </span>
                ) : <span className="text-slate-300">—</span>}
              </td>
              <td className="px-4 py-3 text-center">
                {b.keluar_periode > 0 ? (
                  <span className="inline-flex items-center gap-1 text-rose-600 font-bold text-base">
                    <ArrowUpFromLine className="h-4 w-4" /> {fmtQty(b.keluar_periode)}
                  </span>
                ) : <span className="text-slate-300">—</span>}
              </td>
              <td className="px-4 py-3 text-right">
                <span className={`text-2xl font-extrabold tabular-nums ${b.menipis ? "text-rose-600" : "text-slate-900"}`}>
                  {fmtQty(b.stok_akhir)}
                </span>
                {b.satuan && <span className="text-xs text-slate-400 ml-1">{b.satuan}</span>}
              </td>
              <td className="px-4 py-3 text-center">
                {b.menipis ? (
                  <span className="inline-flex items-center gap-1 rounded-full bg-rose-100 border border-rose-200 px-2.5 py-1 text-[11px] font-bold text-rose-600"
                    data-testid={`live-stock-menipis-${b.kode}`}>
                    <AlertTriangle className="h-3 w-3" /> MENIPIS
                  </span>
                ) : (
                  <span className="inline-flex rounded-full bg-emerald-50 border border-emerald-200 px-2.5 py-1 text-[11px] font-bold text-emerald-600">
                    AMAN
                  </span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Slide 3: seluruh stok gudang sebagai kartu KPI (ikon, tren, nilai besar, min & status)
export function SlideGudang({ gudang, onCariAktif }) {
  const fmt = (n) => Number(n ?? 0).toLocaleString("id-ID", { maximumFractionDigits: 2 });
  const [cari, setCari] = useState("");
  const [scanOpen, setScanOpen] = useState(false);
  const [detail, setDetail] = useState(null); // barang yg diklik utk popup riwayat
  const ubahCari = (v) => {
    setCari(v);
    onCariAktif?.(v.trim().length > 0); // jeda rotasi slide selama pencarian aktif
  };
  const q = cari.trim().toLowerCase();
  // Sembunyikan barang yang tidak aktif > 3 bulan (tidak ada transaksi 90 hari terakhir)
  const cutoff90 = new Date(Date.now() - 90 * 86400000).toISOString().slice(0, 10);
  const aktifGudang = gudang.filter((b) => (b.aktif_terakhir || "") >= cutoff90);
  const tampil = q
    ? aktifGudang.filter((b) =>
        (b.nama || "").toLowerCase().includes(q) ||
        (b.kode || "").toLowerCase().includes(q) ||
        (b.barcode || "").toLowerCase() === q)
    : aktifGudang;

  const bukaDetail = (b) => {
    setDetail(b);
    onCariAktif?.(true); // jeda rotasi selama popup riwayat terbuka
  };
  const tutupDetail = (v) => {
    if (!v) {
      setDetail(null);
      onCariAktif?.(cari.trim().length > 0);
    }
  };

  const bukaScan = () => {
    setScanOpen(true);
    onCariAktif?.(true); // jeda rotasi selama dialog pindai terbuka
  };
  const tutupScan = (v) => {
    setScanOpen(v);
    if (!v) onCariAktif?.(cari.trim().length > 0);
  };
  const hasilScan = (text) => {
    const t = text.toLowerCase();
    const item = gudang.find((b) =>
      (b.barcode || "").toLowerCase() === t || (b.kode || "").toLowerCase() === t);
    if (item) {
      ubahCari(item.kode);
      setScanOpen(false);
      toast.success(`Ditemukan: ${item.kode} · ${item.nama}`);
    } else {
      toast.error(`Barang dengan barcode/kode "${text}" tidak ditemukan di gudang.`);
    }
  };
  return (
    <div key="slide-gudang" data-testid="live-stock-slide-gudang" className="animate-in fade-in duration-200">
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <h2 className="text-base font-extrabold tracking-wide text-emerald-700 flex items-center gap-2">
          <Package className="h-5 w-5" /> STOK GUDANG — {q ? `${tampil.length} / ` : ""}{gudang.length} BARANG (REALTIME)
        </h2>
        <div className="relative ml-auto w-full sm:w-72">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
          <input type="text" value={cari} onChange={(e) => ubahCari(e.target.value)}
            placeholder="Cari kode / nama barang..."
            data-testid="gudang-cari-input"
            className="h-9 w-full rounded-full border border-slate-300 bg-white pl-9 pr-9 text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500" />
          {q && (
            <button type="button" onClick={() => ubahCari("")} data-testid="gudang-cari-clear"
              className="absolute right-2 top-1/2 -translate-y-1/2 h-5 w-5 rounded-full bg-slate-200 text-slate-500 hover:bg-slate-300 flex items-center justify-center">
              <X className="h-3 w-3" />
            </button>
          )}
        </div>
        <button type="button" onClick={bukaScan} data-testid="gudang-scan-button"
          title="Pindai barcode barang untuk langsung menemukan kartunya"
          className="h-9 px-4 rounded-full text-xs font-bold border border-emerald-300 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 inline-flex items-center gap-1.5 transition-colors">
          <ScanBarcode className="h-4 w-4" /> Pindai
        </button>
      </div>
      {tampil.length === 0 && (
        <div className="py-16 text-center text-slate-400" data-testid="gudang-cari-kosong">
          <Search className="h-10 w-10 mx-auto mb-3 text-slate-300" />
          Tidak ada barang yang cocok dengan "{cari.trim()}".
        </div>
      )}
      <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-5 gap-3">
        {tampil.map((b) => (
          <button type="button" key={b.id} data-testid={`gudang-card-${b.kode}`}
            onClick={() => bukaDetail(b)}
            title="Klik untuk melihat riwayat masuk/keluar barang"
            className={`text-left rounded-2xl border bg-white p-4 shadow-sm transition-transform hover:-translate-y-0.5 cursor-pointer focus:outline-none focus:ring-2 focus:ring-emerald-500 ${
              b.menipis ? "border-rose-400 ring-2 ring-rose-300 bg-rose-50 animate-pulse" : "border-slate-200"}`}>
            <div className="flex items-start justify-between gap-2">
              <div className={`h-9 w-9 rounded-xl flex items-center justify-center ${
                b.menipis ? "bg-rose-50 text-rose-500" : "bg-emerald-50 text-emerald-600"}`}>
                <Package className="h-4 w-4" />
              </div>
              <div className="flex flex-col items-end gap-0.5">
                {Number(b.masuk) > 0 && (
                  <span className="inline-flex items-center gap-0.5 text-[11px] font-bold text-emerald-600">
                    +{fmt(b.masuk)} <ArrowDownToLine className="h-3 w-3" />
                  </span>
                )}
                {Number(b.keluar) > 0 && (
                  <span className="inline-flex items-center gap-0.5 text-[11px] font-bold text-rose-500">
                    -{fmt(b.keluar)} <ArrowUpFromLine className="h-3 w-3" />
                  </span>
                )}
              </div>
            </div>
            <p className="mt-2.5 text-[11px] font-medium text-slate-500 leading-snug line-clamp-2 min-h-[28px]">
              <span className="font-mono text-slate-400">{b.kode}</span> · {b.nama}
            </p>
            <p className={`mt-1 text-3xl font-extrabold tabular-nums leading-none ${
              b.menipis ? "text-rose-600" : "text-slate-900"}`}>
              {fmt(b.stok_akhir)}<span className="text-xs font-semibold text-slate-400 ml-1">{b.satuan || ""}</span>
            </p>
            <div className="mt-1.5 flex items-center justify-between">
              <p className="text-[11px] text-slate-400">Min: {fmt(b.stok_minimum)}</p>
              <span className={`rounded-full px-2 py-0.5 text-[9px] font-bold border ${
                b.menipis ? "bg-rose-50 text-rose-600 border-rose-200" : "bg-emerald-50 text-emerald-600 border-emerald-200"}`}>
                {b.menipis ? "MENIPIS" : "AMAN"}
              </span>
            </div>
          </button>
        ))}
      </div>
      <GudangScanDialog open={scanOpen} onOpenChange={tutupScan} onDetected={hasilScan} />
      <GudangRiwayatDialog barang={detail} onOpenChange={tutupDetail} />
    </div>
  );
}
